import {AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild} from '@angular/core';
import {Serializer} from "../../../shared/interfaces/serializer";
import {Space} from "../../../shared/interfaces/space";
import {AppService} from "../../../shared/services/app.service";
import {Observable} from "rxjs";
import {DialogConfig} from "../../../shared/interfaces/dialog";
import {MatSlideToggleChange} from "@angular/material/slide-toggle";
import {DialogConfirmComponent} from "../../../shared/components/dialog/dialog-confirm/dialog-confirm.component";
import {AgGridTemplateFrameworkComponent} from "../../../shared/components/ag-grid/ag-grid-template-framework/ag-grid-template-framework.component";
import {MatDrawer} from "@angular/material/sidenav";
import {DataSourcesService} from "../../../shared/services/data-sources.service";
import {IframeDialogComponent} from "../data-sources-listing/iframe-dialog/iframe-dialog.component";
import {TranslateService} from "@ngx-translate/core";
import {Store} from "@ngrx/store";
import {AppState} from "../../../shared/store/store";
import {
  deleteCustomImport,
  deleteCustomImportSuccess,
  updateCustomDataSource
} from "../../../shared/store/dataSourcesCustom/dataSourcesCustom.actions";
import {
  selectDataSourceCustomComponentObjects,
  selectDataSourcesCustomsLoading
} from "../../../shared/store/dataSourcesCustom/dataSourcesCustom.selectors";
import {selectSpace, selectIsSuperAdmin} from "../../../shared/store/init/init.selectors";
import {
  AgGridComponentFrameworkComponent
} from "../../../shared/components/ag-grid/ag-grid-component-framework/ag-grid-component-framework.component";
import {CellImportedDataComponent} from "../data-sources-listing/cell-imported-data/cell-imported-data.component";
import {dialogOpen} from "../../../shared/store/dialog/dialog.actions";
import {DatasetUsageComponentObject} from "../../../shared/classes/dataset-usage-component-object";
import {DataSource} from "../../../shared/interfaces/data-source";
import {selectDataSourceCustom} from "../../../shared/store/dataSourcesAll/dataSourcesAll.selectors";
import {filter, first} from "rxjs/operators";

@Component({
  selector: 'app-custom-sources',
  templateUrl: './custom-sources.component.html',
  styleUrls: ['./custom-sources.component.scss']
})
export class CustomSourcesComponent implements AfterViewInit, OnDestroy {
  @ViewChild('actions') private readonly actionsT: TemplateRef<any>;
  @ViewChild('drawerActions') private readonly drawerActionsT: TemplateRef<any>;
  @ViewChild('drawerImportDates') private readonly drawerImportedDatesT: TemplateRef<any>;
  @ViewChild('logo') private readonly logoT: TemplateRef<any>;
  @ViewChild('imported') private readonly importedT: TemplateRef<any>;
  @ViewChild('enabled') private readonly enabledT: TemplateRef<any>;
  @ViewChild('desc') private readonly descT: TemplateRef<any>;
  @ViewChild('status') private readonly statusT: TemplateRef<any>;
  @ViewChild('drawer') private readonly drawer: MatDrawer;

  public columnDefs: Array<any> = [
    {
      field:                 'data-source',
      minWidth:              300,
      maxWidth:              300,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      lockPosition:          true,
      filter:                'agTextColumnFilter',
      headerValueGetter:     _ => {
        return this.translateS.instant('data_sources.data_source');
      },
      cellRendererParams:    () => {
        return {
          ngTemplate: this.logoT
        };
      },
      filterValueGetter:     params => params.data.attributes.name,
    },
    {
      field: 'desc',
      minWidth: 450,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      lockPosition: true,
      suppressMenu: true,
      flex: 1,
      headerValueGetter: _ => {
        return this.translateS.instant('sideNav.settings.settings');
      },
      cellRendererParams: _ => {
        return {
          ngTemplate: this.descT
        };
      }
    },
    {
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      field:                 'imported',
      minWidth:              220,
      maxWidth:              220,
      lockPosition:          true,
      suppressMenu:          true,
      headerValueGetter:     _ => {
        return this.translateS.instant('data_sources.imported');
      },
      cellRendererParams:    _ => {
        return {
          ngTemplate: this.importedT
        };
      },
    },
    {
      field: 'status',
      filter: true,
      minWidth: 145,
      maxWidth: 145,
      tooltipComponent: 'customTooltip',
      headerTooltip: this.translateS.instant('data_sources.status_tooltip'),
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      lockPosition:          true,
      filterValueGetter:     params => this.translateS.instant(`export.status.${params.data.attributes.status}`),
      headerValueGetter:     _ => {
        return this.translateS.instant('data_sources.status');
      },
      cellRendererParams:    params => {
        return {
          ngTemplate: this.statusT
        };
      }
    },
    {
      width: 200,
      maxWidth: 200,
      minWidth: 200,
      tooltipComponent: 'customTooltip',
      headerTooltip: this.translateS.instant('data_sources.last_14_days_data_tooltip'),
      cellRendererFramework: AgGridComponentFrameworkComponent,
      lockPosition: true,
      suppressMenu: true,
      cellStyle: { overflow: 'unset' },
      headerValueGetter: _ => {
        return this.translateS.instant('data_sources.last_14_days_data');
      },
      cellRendererParams: params => {
        return {
          component: CellImportedDataComponent
        };
      }
    },
    {
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      lockPosition: true,
      suppressMenu: true,
      maxWidth: 90,
      minWidth: 90,
      tooltipComponent: 'customTooltip',
      headerTooltip: this.translateS.instant('data_sources.enabled_tooltip'),
      headerValueGetter: _ => {
        return this.translateS.instant('data_sources.enabled');
      },
      cellRendererParams:    params => {
        return {
          ngTemplate: this.enabledT
        };
      }
    },
    {
      maxWidth: 144,
      minWidth: 144,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      lockPosition: true,
      suppressMenu: true,
      cellRendererParams: params => {
        return {
          ngTemplate: this.actionsT
        };
      }
    }
  ];

  public drawerDataSource: DatasetUsageComponentObject;
  public defaultColDef: any = {
    resizable: true,
    suppressNavigable: true,
    suppressCellSelection: true,
    menuTabs: ['filterMenuTab'],
    filterParams: {
      buttons: ['reset']
    }
  };

  public drawerColumnDefs: Array<any> = [
    {
      minWidth: 200,
      sortable: true,
      filter: true,
      lockPosition: true,
      headerValueGetter: _ => {
        return this.translateS.instant('data_sources.custom_sources.imports.file_name');
      },
      valueGetter: params => {
        return params.data.payload.attributes.original_file_name;
      }
    },
    {
      field: 'importedDates',
      minWidth: 200,
      sortable: true,
      filter: true,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      lockPosition: true,
      cellRendererParams: params => {
        return {
          ngTemplate: this.drawerImportedDatesT
        };
      }
    },
    {
      field: 'status',
      filter: true,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      lockPosition: true,
      filterValueGetter: params => this.translateS.instant(`export.status.${params.data.payload.attributes.status}`),
      headerValueGetter: _ => {
        return this.translateS.instant('data_sources.status');
      },
      cellRendererParams: params => {
        return {
          ngTemplate: this.statusT
        };
      }
    },
    {
      maxWidth: 150,
      minWidth: 150,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      lockPosition: true,
      suppressMenu: true,
      cellRendererParams: params => {
        return {
          ngTemplate: this.drawerActionsT
        };
      }
    }
  ];

  public dialogOpenIframeConfig: DialogConfig = {
    data: {
      component: IframeDialogComponent
    },
    width: '650px',
    height: '600px',
    disableClose: false
  };

  public dataSourceCustom: Serializer<DataSource>;
  public dataSourcesCustom$: Observable<Array<DatasetUsageComponentObject>> = this._store.select(selectDataSourceCustomComponentObjects);
  public dataSourcesCustomLoading$: Observable<boolean> = this._store.select(selectDataSourcesCustomsLoading);
  public space$: Observable<Serializer<Space>> = this._store.select(selectSpace);
  public isSuperAdmin$: Observable<boolean> = this._store.select(selectIsSuperAdmin);

  constructor(
    public readonly appS: AppService,
    public readonly translateS: TranslateService,
    public readonly dataSourcesS: DataSourcesService,
    private readonly _store: Store<AppState>
  ) {}

  ngAfterViewInit(): void {
    this._store
      .select(selectDataSourceCustom)
      .pipe(
        filter((datasource: Serializer<DataSource>): boolean => !!datasource),
        first()
      )
      .subscribe((datasource: Serializer<DataSource>): void => {
        this.dataSourceCustom = datasource;
      });
  }

  ngOnDestroy(): void {}

  public onCustomImportFilter(row: DatasetUsageComponentObject): void {
    if (this.drawerDataSource != row) {
      this.drawerDataSource = row;
      this.drawer.open();
    } else {
      this.drawer.toggle();
    }
  }

  public enabledToggled(event: MatSlideToggleChange, data_set_usage: DatasetUsageComponentObject) {
    this._store.dispatch(updateCustomDataSource({
      dataSource: data_set_usage.payload,
      update: { enabled: event.checked }
    }));
  }

  public onDelete(customImport) {
    this._store.dispatch(dialogOpen({
      component: DialogConfirmComponent,
      config: {
        width: '500px',
        height: 'auto',
      },
      data: {
        title: 'data_sources.delete_custom_import_title',
        message: 'data_sources.delete_custom_import_message',
        type: 'delete',
        item: customImport,
        startActions: [deleteCustomImport],
        successActions: [deleteCustomImportSuccess],
        onSubmit: () => {
          this._store.dispatch(deleteCustomImport({
            customImport: customImport
          }));
        }
      }
    }));
  }
}
